import { useEffect } from 'react';
import type { GetStaticPropsContext } from 'next';
import { useQuery } from '@sanity/react-loader';
import { groq } from 'next-sanity';

import { fetchResourcePaths } from '#/lib/sanity/fetchers/fetchResourcePaths';
import { ResourceProps as ResourcePropsSchema } from '#/lib/sanity/fetchers/fetchResourceProps';
import {
  checkPageSchema,
  type NextPageData,
} from '#/lib/sanity/fetchers/helpers';
import {
  loadQuery,
  loadQueryOptions,
  type QueryResponseInitial,
} from '#/lib/sanity/loader';
import {
  resource as resourceQuery,
  SanityResource,
} from '#/lib/sanity/queries/resource';
import { site } from '#/lib/sanity/queries/site';
import { useAnalytics } from '#/lib/segment';
import { addViewedAlgoliaObjectID } from '#/lib/viewed-algolia-objects';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';
import { Calculator } from '#/components/Calculator';
import { Footnotes } from '#/components/Footnotes';
import { FormCustom } from '#/components/Form/Custom';
import { Headline } from '#/components/Headline';
import { Media } from '#/components/Media';
import { PageSection } from '#/components/PageSection';
import { RelatedContent } from '#/components/RelatedContent';
import { BreadcrumbSchema } from '#/components/SeoSchema/BreadcrumbSchema';
import { ResourceSchema } from '#/components/SeoSchema/ResourceSchema';
import { ShareButtons } from '#/components/ShareButtons';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchResourcePaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

const resourcePageQuery = groq`{
  'page': ${resourceQuery},
  'site': ${site},
}`;

type ResourceQueryResult = Omit<
  NextPageData<typeof SanityResource>,
  'draftMode'
>;

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  const initial = await loadQuery<ResourceQueryResult>(
    resourcePageQuery,
    { slug: params!.slug },
    loadQueryOptions({ draftMode }),
  );
  const data = await checkPageSchema<typeof SanityResource>({
    schema: ResourcePropsSchema,
    result: initial.data,
    draftMode,
    context: { callerName: 'resource getStaticProps', slug: params!.slug },
  });
  const props = { ...data, draftMode: !!draftMode };

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props: { ...props, initial } };
}

export default function Resource(
  props: NextPageData<typeof SanityResource> & {
    initial: QueryResponseInitial<ResourceQueryResult>;
  },
) {
  const { data } = useQuery<ResourceQueryResult>(
    resourcePageQuery,
    { slug: props.page?.slug ?? '' },
    { initial: props.initial },
  );
  const resource = data.page ?? props.page;

  const analytics = useAnalytics();

  // Track the viewed resource in local storage - used for Algolia search re-ranking
  useEffect(() => {
    if (!resource) return;
    addViewedAlgoliaObjectID({
      objectID: resource._id,
      algoliaObjectType: 'RESOURCES',
    });
    analytics.track('Viewed Resource', {
      resourceTitle: resource.title,
      resourceId: resource._id,
    });
  }, [resource, analytics]);

  if (!resource) {
    return null;
  }

  return (
    <div>
      <BreadcrumbSchema
        items={[
          { name: 'Resources', path: '/resources' },
          { name: resource.category.title, path: resource.category.href },
          { name: resource.title, path: resource.href },
        ]}
      />
      <ResourceSchema {...resource} />
      {!resource.hideHeroSection && (
        <div className="p-main mb-20 mt-[calc(var(--header-height)*-1)] rounded-b-20 bg-grayscale-01 pb-40 pt-[calc(var(--header-height)+3rem)] m:rounded-b-35 m:pb-60 l:mb-60">
          <div className="grid-main mx-auto max-w-1235 gap-y-30">
            <div className="col-span-full space-y-25 m:col-span-6">
              <p className="inline-block rounded-full bg-electric px-10 pb-2 text-14 font-normal leading-150">
                {resource.category.title}
              </p>
              <Headline value={resource.title} variant={3} />
              <p className="flex text-12 text-grayscale-05 l:text-14">
                {new Date(resource.publishedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                <span className="px-7">•</span>
                {resource.readTime} min read
              </p>
              <BasicText
                value={resource.introduction}
                className="max-w-480 text-pretty text-16 leading-150 text-grayscale-06 [&_p+p]:mt-20"
              />
              {resource.heroButton?.href ? (
                <Button
                  className="mt-35"
                  href={resource.heroButton.href}
                  target={resource.heroButton.target}
                >
                  {resource.heroButton.title}
                </Button>
              ) : null}
            </div>
            <div className="col-span-full m:col-span-6 m:pt-48">
              {resource.hasHeroForm &&
              resource.heroForm &&
              resource.heroFormConfirmation ? (
                <div className="ml-auto pb-40 m:max-w-480">
                  <FormCustom
                    form={resource.heroForm}
                    confirmation={resource.heroFormConfirmation}
                  />
                </div>
              ) : resource.heroMedia ? (
                <Media
                  media={resource.heroMedia}
                  sizes={['100vw', { m: '50vw', '1285px': '618px' }]}
                  className="rounded-15 m:rounded-25"
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {resource.sections.map((section, index) => {
        return (
          <div key={section._key} className="relative">
            {section._type === 'calculator' && (
              <Calculator
                {...section}
                resourceTitle={resource.title}
                resourceId={resource._id}
              />
            )}
            {section._type !== 'calculator' && (
              <PageSection
                key={section._key}
                section={section}
                // Resource hero isn't a PageSection; offset so dark modules
                // don't invert the nav over the light hero.
                index={resource.hideHeroSection ? index : index + 1}
                isPreview={props.draftMode}
              />
            )}
          </div>
        );
      })}
      {!resource.hideShareButtons && (
        <div className="mx-auto max-w-1235 my-30" data-share-buttons>
          <ShareButtons
            headline={resource.shareLabel ?? 'Share this resource'}
            href={`/resources/${resource.slug}`}
            title={resource.title}
            className=""
          />
        </div>
      )}
      {resource.relatedContent ? (
        <RelatedContent {...resource.relatedContent} />
      ) : null}
      {resource.footnotes ? <Footnotes {...resource.footnotes} /> : null}
    </div>
  );
}
